import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { 
  Truck, 
  ArrowLeft,
  Save,
  Hash,
  Calendar,
  FileText,
  Plus,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export const VehiclePage = () => {
  const navigate = useNavigate();
  const { get, put } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [vehicle, setVehicle] = useState({
    unit_number: '',
    plate_number: '',
    make: '',
    model: '',
    year: '',
    vin: '',
    trailers: [] // Array of trailers
  });

  useEffect(() => {
    fetchVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVehicle = async () => {
    try {
      const result = await get('/driver/vehicle');
      if (result.success && result.data) {
        // Migration: convert old format to new format
        const data = result.data;
        if (data.trailer_number && !data.trailers) {
          data.trailers = [{
            id: '1',
            number: data.trailer_number,
            plate: data.trailer_plate || ''
          }];
        }
        setVehicle(prev => ({ ...prev, ...data, trailers: data.trailers || [] }));
      }
    } catch (err) {
      console.error('Error fetching vehicle:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!vehicle.unit_number.trim()) {
      toast.error('Le numéro d\'unité est requis');
      return;
    }

    setSaving(true);
    try {
      const result = await put('/driver/vehicle', vehicle);
      if (result.success) {
        toast.success('Véhicule enregistré!');
        navigate('/profile');
      } else {
        toast.error(result.error || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setVehicle(prev => ({ ...prev, [field]: value }));
  };

  // Trailer management
  const addTrailer = () => {
    const newTrailer = {
      id: Date.now().toString(),
      number: '',
      plate: ''
    };
    setVehicle(prev => ({
      ...prev,
      trailers: [...prev.trailers, newTrailer]
    }));
  };

  const removeTrailer = (trailerId) => {
    setVehicle(prev => ({
      ...prev,
      trailers: prev.trailers.filter(t => t.id !== trailerId)
    }));
  };

  const updateTrailer = (trailerId, field, value) => {
    setVehicle(prev => ({
      ...prev,
      trailers: prev.trailers.map(t => 
        t.id === trailerId ? { ...t, [field]: value } : t
      )
    }));
  };

  if (loading) {
    return (
      <div className="page-wrapper flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-wrapper" data-testid="vehicle-page">
      {/* Header */}
      <div className="app-header">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/profile')}
            className="p-2 hover:bg-slate-800 rounded-lg"
          >
            <ArrowLeft className="text-slate-400" size={24} />
          </button>
          <div className="flex items-center gap-3">
            <Truck className="text-amber-500" size={28} />
            <div>
              <h1 className="text-2xl text-white">Mon véhicule</h1>
              <p className="text-slate-400 text-sm">Tracteur et remorques</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Tracteur Section */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <h2 className="text-lg font-bold text-amber-500 mb-4 flex items-center gap-2">
            <Truck size={20} />
            TRACTEUR
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="label-uppercase">Numéro d'unité *</label>
              <div className="relative mt-1">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  value={vehicle.unit_number}
                  onChange={(e) => handleChange('unit_number', e.target.value)}
                  placeholder="Ex: 101"
                  className="pl-10 bg-slate-950 border-slate-800 text-white h-12"
                  data-testid="unit-number"
                />
              </div>
            </div>

            <div>
              <label className="label-uppercase">Plaque d'immatriculation</label>
              <div className="relative mt-1">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                <Input
                  value={vehicle.plate_number}
                  onChange={(e) => handleChange('plate_number', e.target.value.toUpperCase())}
                  placeholder="Ex: ABC 123"
                  className="pl-10 bg-slate-950 border-slate-800 text-white h-12 uppercase"
                  data-testid="plate-number"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-uppercase">Marque</label>
                <Input
                  value={vehicle.make}
                  onChange={(e) => handleChange('make', e.target.value)}
                  placeholder="Ex: Freightliner"
                  className="mt-1 bg-slate-950 border-slate-800 text-white h-12"
                />
              </div>
              <div>
                <label className="label-uppercase">Modèle</label>
                <Input
                  value={vehicle.model}
                  onChange={(e) => handleChange('model', e.target.value)}
                  placeholder="Ex: Cascadia"
                  className="mt-1 bg-slate-950 border-slate-800 text-white h-12"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-uppercase">Année</label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    type="number"
                    value={vehicle.year}
                    onChange={(e) => handleChange('year', e.target.value)}
                    placeholder="2024"
                    className="pl-10 bg-slate-950 border-slate-800 text-white h-12"
                  />
                </div>
              </div>
              <div>
                <label className="label-uppercase">NIV (VIN)</label>
                <Input
                  value={vehicle.vin}
                  onChange={(e) => handleChange('vin', e.target.value.toUpperCase())}
                  placeholder="17 caractères"
                  maxLength={17}
                  className="mt-1 bg-slate-950 border-slate-800 text-white h-12 uppercase"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Remorques Section - Dynamic */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-blue-400 flex items-center gap-2">
              <Truck size={20} />
              REMORQUES ({vehicle.trailers.length})
            </h2>
            <Button
              onClick={addTrailer}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-trailer-btn"
            >
              <Plus size={18} className="mr-1" />
              Ajouter
            </Button>
          </div>

          {vehicle.trailers.length === 0 ? (
            <p className="text-slate-500 text-center py-4">
              Aucune remorque configurée.<br />
              <span className="text-sm">Cliquez sur "Ajouter" pour configurer une remorque.</span>
            </p>
          ) : (
            <div className="space-y-4">
              {vehicle.trailers.map((trailer, index) => (
                <div 
                  key={trailer.id} 
                  className="bg-slate-800/50 rounded-lg p-3 border border-slate-700"
                  data-testid={`trailer-${index}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-blue-300">
                      Remorque #{index + 1}
                    </span>
                    <button
                      onClick={() => removeTrailer(trailer.id)}
                      className="p-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded"
                      data-testid={`remove-trailer-${index}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Numéro</label>
                      <div className="relative mt-1">
                        <Hash className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                        <Input
                          value={trailer.number}
                          onChange={(e) => updateTrailer(trailer.id, 'number', e.target.value)}
                          placeholder="R-501"
                          className="pl-8 bg-slate-950 border-slate-700 text-white h-10 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 uppercase">Plaque</label>
                      <div className="relative mt-1">
                        <FileText className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
                        <Input
                          value={trailer.plate}
                          onChange={(e) => updateTrailer(trailer.id, 'plate', e.target.value.toUpperCase())}
                          placeholder="XYZ 789"
                          className="pl-8 bg-slate-950 border-slate-700 text-white h-10 text-sm uppercase"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <p className="text-xs text-slate-500 mt-3 text-center">
            B-Train: ajoutez 2 remorques • Changement durant la journée: ajoutez les remorques utilisées
          </p>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          className="w-full h-14 btn-primary-glow"
          disabled={saving}
          data-testid="save-vehicle"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Enregistrement...
            </span>
          ) : (
            <>
              <Save size={20} className="mr-2" />
              Enregistrer le véhicule
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default VehiclePage;
